import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { API_URL } from '../../../api/api';
const BranchReportLayer = () => {
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const rowsPerPage = 10;

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchReportData();
  }, [token]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const res = await axios.get(`${API_URL}/getbranchReport`);
      setReportData(res.data);
      setFilteredData(res.data);
    } catch (err) {
      console.error('Failed to fetch report:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter by search term
  useEffect(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = reportData.filter(group =>
      group.group_name?.toLowerCase().includes(lowerSearch)
    );
    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchTerm, reportData]);

  const handleExport = () => {
    const exportData = filteredData.map(group => {
      const total_cash_loan = group.total_cash_loan ?? 0;
      const cashemireceived = group.total_cash_payments ?? 0;
      const productemireceived = group.total_product_payments ?? 0;
      const total_product_loan = group.total_product_loan ?? 0;
      const total_cash_emi = group.total_cash_emi ?? 0;
      const total_product_emi = group.total_product_emi ?? 0;
      const remainingCash = total_cash_loan - cashemireceived;
      const remainingProduct = total_product_loan - productemireceived;
      const remainingTotal = remainingCash + remainingProduct;

      return {
        'Group': group.group_name,
        'Cash Loan': total_cash_loan,
        'Product Loan': total_product_loan,
        'Cash EMI': total_cash_emi,
        'Product EMI': total_product_emi,
        'Cash Received': cashemireceived,
        'Product Received': productemireceived,
        'Cash Balance': remainingCash,
        'Product Balance': remainingProduct,
        'Total Balance': remainingTotal,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'BranchReport');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    const now = new Date();
    const pad = n => n.toString().padStart(2, '0');
    const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}`;
    saveAs(blob, `Branch_Report_${timestamp}.xlsx`);
  };

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = a[sortConfig.key] ?? 0;
    const bVal = b[sortConfig.key] ?? 0;
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Pagination
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentData = sortedData.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Get sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Calculate grand totals
  const calculateTotals = () => {
    let totalCashLoan = 0;
    let totalProductLoan = 0;
    let totalCashEmi = 0;
    let totalProductEmi = 0;
    let totalCashReceived = 0;
    let totalProductReceived = 0;
    let totalCashRemaining = 0;
    let totalProductRemaining = 0;
    let totalOverallRemaining = 0;

    filteredData.forEach(group => {
      const cashLoan = group.total_cash_loan || 0;
      const productLoan = group.total_product_loan || 0;
      const cashEmi = group.total_cash_emi || 0;
      const productEmi = group.total_product_emi || 0;
      const cashReceived = group.total_cash_payments || 0;
      const productReceived = group.total_product_payments || 0;

      totalCashLoan += cashLoan;
      totalProductLoan += productLoan;
      totalCashEmi += cashEmi;
      totalProductEmi += productEmi;
      totalCashReceived += cashReceived;
      totalProductReceived += productReceived;

      const cashRemaining = cashLoan - cashReceived;
      const productRemaining = productLoan - productReceived;

      totalCashRemaining += cashRemaining;
      totalProductRemaining += productRemaining;
      totalOverallRemaining += cashRemaining + productRemaining;
    });

    return {
      totalCashLoan,
      totalProductLoan,
      totalCashEmi,
      totalProductEmi,
      totalCashReceived,
      totalProductReceived,
      totalCashRemaining,
      totalProductRemaining,
      totalOverallRemaining
    };
  };

  const totals = calculateTotals();

  return (
    <div className="branch-report-container">
      {/* Header */}
      <div className="report-header">
        <div className="header-left">
          <h1 className="report-title">Branch Report</h1>
          <p className="report-subtitle">Overview of all branch groups and financial details</p>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="export-btn" onClick={handleExport}>
            <span className="export-icon">📊</span>
            Export Excel
          </button>
          <button className="refresh-btn" onClick={fetchReportData}>
            <span className="refresh-icon">↻</span>
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="stats-summary">
        <div className="stat-card">
          <div className="stat-icon cash">💰</div>
          <div className="stat-details">
            <h3 className="stat-value">
              {formatCurrency(totals.totalCashLoan)}
            </h3>
            <p className="stat-label">Total Cash Loans</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon product">📦</div>
          <div className="stat-details">
            <h3 className="stat-value">
              {formatCurrency(totals.totalProductLoan)}
            </h3>
            <p className="stat-label">Total Product Loans</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon received">💳</div>
          <div className="stat-details">
            <h3 className="stat-value">
              {formatCurrency(totals.totalCashReceived)}
            </h3>
            <p className="stat-label">Cash Received</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon received">📦</div>
          <div className="stat-details">
            <h3 className="stat-value">
              {formatCurrency(totals.totalProductReceived)}
            </h3>
            <p className="stat-label">Product Received</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon balance">⚖️</div>
          <div className="stat-details">
            <h3 className="stat-value">
              {formatCurrency(totals.totalOverallRemaining)}
            </h3>
            <p className="stat-label">Total Balance</p>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="table-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading report data...</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="report-table">
                <thead>
                  <tr>
                    <th className="serial-col">#</th>
                    <th className="group-col" onClick={() => handleSort('group_name')}>
                      Group Name {getSortIndicator('group_name')}
                    </th>
                    <th className="amount-col" onClick={() => handleSort('total_cash_loan')}>
                      Cash Loan {getSortIndicator('total_cash_loan')}
                    </th>
                    <th className="amount-col" onClick={() => handleSort('total_product_loan')}>
                      Product Loan {getSortIndicator('total_product_loan')}
                    </th>
                    <th className="amount-col" onClick={() => handleSort('total_cash_emi')}>
                      Cash EMI {getSortIndicator('total_cash_emi')}
                    </th>
                    <th className="amount-col" onClick={() => handleSort('total_product_emi')}>
                      Product EMI {getSortIndicator('total_product_emi')}
                    </th>
                    <th className="received-col" onClick={() => handleSort('total_cash_payments')}>
                      Cash Received {getSortIndicator('total_cash_payments')}
                    </th>
                    <th className="received-col" onClick={() => handleSort('total_product_payments')}>
                      Product Received {getSortIndicator('total_product_payments')}
                    </th>
                    <th className="balance-col">Cash Balance</th>
                    <th className="balance-col">Product Balance</th>
                    <th className="balance-col">Total Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.length === 0 ? (
                    <tr className="no-data-row">
                      <td colSpan="11">
                        <div className="no-data">
                          <span className="no-data-icon">📊</span>
                          <p>No groups found</p>
                          {searchTerm && <small>Try adjusting your search term</small>}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentData.map((group, index) => {
                      const total_cash_loan = group.total_cash_loan ?? 0;
                      const cashemireceived = group.total_cash_payments ?? 0;
                      const productemireceived = group.total_product_payments ?? 0;
                      const total_product_loan = group.total_product_loan ?? 0;
                      const total_cash_emi = group.total_cash_emi ?? 0;
                      const total_product_emi = group.total_product_emi ?? 0;
                      const remainingCash = total_cash_loan - cashemireceived;
                      const remainingProduct = total_product_loan - productemireceived;
                      const remainingTotal = remainingCash + remainingProduct;

                      return (
                        <tr key={group.group_id} className="data-row">
                          <td className="serial">{indexOfFirst + index + 1}</td>
                          <td className="group-name">
                            <span className="group-icon">👥</span>
                            {group.group_name}
                          </td>
                          <td className="amount cash-amount">{formatCurrency(total_cash_loan)}</td>
                          <td className="amount product-amount">{formatCurrency(total_product_loan)}</td>
                          <td className="amount emi-amount">{formatCurrency(total_cash_emi)}</td>
                          <td className="amount emi-amount">{formatCurrency(total_product_emi)}</td>
                          <td className="received cash-received">{formatCurrency(cashemireceived)}</td>
                          <td className="received product-received">{formatCurrency(productemireceived)}</td>
                          <td className={`balance ${remainingCash > 0 ? 'negative' : 'positive'}`}>
                            {formatCurrency(remainingCash)}
                            {remainingCash > 0 && <span className="balance-badge">Due</span>}
                          </td>
                          <td className={`balance ${remainingProduct > 0 ? 'negative' : 'positive'}`}>
                            {formatCurrency(remainingProduct)}
                            {remainingProduct > 0 && <span className="balance-badge">Due</span>}
                          </td>
                          <td className={`balance ${remainingTotal > 0 ? 'negative' : 'positive'}`}>
                            {formatCurrency(remainingTotal)}
                            {remainingTotal > 0 && <span className="balance-badge">Due</span>}
                          </td>
                        </tr>
                      );
                    })
                  )}

                  {/* Grand Totals Row */}
                  {currentData.length > 0 && (
                    <tr className="grand-total-row">
                      <td colSpan="2" className="grand-total-label">
                        <strong>GRAND TOTALS</strong>
                      </td>
                      <td className="amount cash-amount grand-total">
                        <strong>{formatCurrency(totals.totalCashLoan)}</strong>
                      </td>
                      <td className="amount product-amount grand-total">
                        <strong>{formatCurrency(totals.totalProductLoan)}</strong>
                      </td>
                      <td className="amount emi-amount grand-total">
                        <strong>{formatCurrency(totals.totalCashEmi)}</strong>
                      </td>
                      <td className="amount emi-amount grand-total">
                        <strong>{formatCurrency(totals.totalProductEmi)}</strong>
                      </td>
                      <td className="received cash-received grand-total">
                        <strong>{formatCurrency(totals.totalCashReceived)}</strong>
                      </td>
                      <td className="received product-received grand-total">
                        <strong>{formatCurrency(totals.totalProductReceived)}</strong>
                      </td>
                      <td className={`balance ${totals.totalCashRemaining > 0 ? 'negative' : 'positive'} grand-total`}>
                        <strong>{formatCurrency(totals.totalCashRemaining)}</strong>
                      </td>
                      <td className={`balance ${totals.totalProductRemaining > 0 ? 'negative' : 'positive'} grand-total`}>
                        <strong>{formatCurrency(totals.totalProductRemaining)}</strong>
                      </td>
                      <td className={`balance ${totals.totalOverallRemaining > 0 ? 'negative' : 'positive'} grand-total`}>
                        <strong>{formatCurrency(totals.totalOverallRemaining)}</strong>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredData.length > 0 && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredData.length)} of {filteredData.length} groups
                </div>
                <div className="pagination-controls">
                  <button
                    className="pagination-btn prev"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                  >
                    ← Previous
                  </button>
                  <div className="page-numbers">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return pageNum <= totalPages ? (
                        <button
                          key={pageNum}
                          className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      ) : null;
                    })}
                    {totalPages > 5 && (
                      <>
                        {currentPage < totalPages - 2 && <span className="page-dots">...</span>}
                        <button
                          className={`page-btn ${currentPage === totalPages ? 'active' : ''}`}
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </div>
                  <button
                    className="pagination-btn next"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <style jsx>{`
        .branch-report-container {
          padding: 24px !important;
          min-height: 100vh !important;
        }

        /* Header Styles */
        .report-header {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          margin-bottom: 30px !important;
          flex-wrap: wrap !important;
          gap: 20px !important;
        }

        .header-left {
          flex: 1 !important;
          min-width: 300px !important;
        }

        .report-title {
          font-size: 28px !important;
          font-weight: 700 !important;
          color: #1e293b !important;
          margin: 0 0 8px 0 !important;
        }

        .report-subtitle {
          font-size: 14px !important;
          color: #64748b !important;
          margin: 0 !important;
        }

        .header-actions {
          display: flex !important;
          align-items: center !important;
          gap: 16px !important;
          flex-wrap: wrap !important;
        }

        .search-box {
          position: relative !important;
          min-width: 250px !important;
        }

        .search-icon {
          position: absolute !important;
          left: 14px !important;
          top: 50% !important;
          transform: translateY(-50%) !important;
          color: #94a3b8 !important;
          font-size: 16px !important;
        }

        .search-input {
          width: 100% !important;
          padding: 12px 16px 12px 42px !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 10px !important;
          font-size: 14px !important;
          background: white !important;
          transition: all 0.3s ease;
        }

        .search-input:focus {
          outline: none !important;
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }

        .export-btn {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
          color: white !important;
          border: none !important;
          padding: 12px 24px !important;
          border-radius: 10px !important;
          font-size: 14px !important;
          font-weight: 600 !important;
          cursor: pointer !important;
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
          transition: all 0.3s ease !important;
        }

        .export-btn:hover {
          transform: translateY(-1px) !important;
          box-shadow: 0 10px 20px rgba(16, 185, 129, 0.3) !important;
        }

        .refresh-btn {
          background: #f1f5f9 !important;
          border: 1px solid #e2e8f0 !important;
          color: #64748b !important;
          width: 44px !important;
          height: 44px !important;
          border-radius: 10px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          cursor: pointer !important;
          transition: all 0.3s ease !important;
        }

        .refresh-btn:hover {
          background: #e2e8f0 !important;
          transform: rotate(90deg) !important;
        }

        /* Stats Summary */
        .stats-summary {
          display: grid !important;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)) !important;
          gap: 20px !important;
          margin-bottom: 30px !important;
        }

        .stat-card {
          background: white !important;
          border-radius: 12px !important;
          padding: 24px !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
          display: flex !important;
          align-items: center !important;
          gap: 20px !important;
          transition: transform 0.3s ease !important;
        }

        .stat-card:hover {
          transform: translateY(-2px) !important;
        }

        .stat-icon {
          width: 56px !important;
          height: 56px !important;
          border-radius: 12px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-size: 24px !important;
        }

        .stat-icon.cash { background: linear-gradient(135deg, #fef3c7 0%, #fbbf24 100%); }
        .stat-icon.product { background: linear-gradient(135deg, #dbeafe 0%, #60a5fa 100%); }
        .stat-icon.received { background: linear-gradient(135deg, #dcfce7 0%, #4ade80 100%); }
        .stat-icon.balance { background: linear-gradient(135deg, #f3e8ff 0%, #a855f7 100%); }

        .stat-details {
          flex: 1 !important;
        }

        .stat-value {
          font-size: 24px !important;
          font-weight: 700 !important;
          color: #1e293b !important;
          margin: 0 0 4px 0 !important;
        }

        .stat-label {
          font-size: 14px !important;
          color: #64748b !important;
          margin: 0 !important;
        }

        /* Table Styles */
        .table-container {
          background: white !important;
          border-radius: 12px !important;
          overflow: hidden !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }

        .loading-state {
          padding: 60px !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          color: #64748b !important;
        }

        .spinner {
          width: 50px !important;
          height: 50px !important;
          border: 3px solid #f1f5f9 !important;
          border-top: 3px solid #3b82f6 !important;
          border-radius: 50% !important;
          animation: spin 1s linear infinite !important;
          margin-bottom: 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .table-responsive {
          overflow-x: auto !important;
        }

        .report-table {
          width: 100% !important;
          border-collapse: separate !important;
          border-spacing: 0;
        }

        .report-table thead {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
        }

        .report-table th {
          padding: 18px 16px !important;
          text-align: left !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          color: #475569 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          border-bottom: 2px solid #e2e8f0 !important;
          cursor: pointer !important;
          user-select: none !important;
          transition: background 0.2s ease !important;
        }

        .report-table th:hover {
          background: #f1f5f9 !important;
        }

        .report-table td {
          padding: 16px !important;
          border-bottom: 1px solid #f1f5f9 !important;
          font-size: 14px !important;
          color: #334155 !important;
        }

        .report-table tr:last-child td {
          border-bottom: none !important;
        }

        .report-table tr:hover {
          background: #f8fafc !important;
        }

        .serial-col { width: 60px !important; }
        .group-col { min-width: 200px !important; }
        .amount-col { min-width: 150px !important; }
        .received-col { min-width: 150px !important; }
        .balance-col { min-width: 150px !important; }

        .serial {
          font-weight: 600 !important;
          color: #64748b !important;
          text-align: center !important;
        }

        .group-name {
          font-weight: 600 !important;
          color: #1e293b !important;
          display: flex !important;
          align-items: center !important;
          gap: 10px !important;
        }

        .group-icon {
          opacity: 0.7 !important;
        }

        .amount, .received, .balance {
          font-weight: 600 !important;
        }

        .cash-amount { color: #f59e0b; }
        .product-amount { color: #3b82f6; }
        .emi-amount { color: #8b5cf6; }
        .cash-received { color: #10b981; }
        .product-received { color: #06b6d4; }

        .balance.negative {
          color: #ef4444;
          font-weight: 700;
        }

        .balance.positive {
          color: #10b981;
        }

        .balance-badge {
          background: #fee2e2 !important;
          color: #dc2626 !important;
          font-size: 11px !important;
          padding: 2px 8px !important;
          border-radius: 12px !important;
          margin-left: 8px !important;
          font-weight: 600 !important;
        }

        .no-data-row td {
          padding: 60px !important;
          text-align: center !important;
        }

        .no-data {
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          color: #94a3b8 !important;
        }

        .no-data-icon {
          font-size: 48px !important;
          margin-bottom: 16px !important;
          opacity: 0.5 !important;
        }

        /* Pagination */
        .pagination-container {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          padding: 20px 24px !important;
          border-top: 1px solid #f1f5f9 !important;
          flex-wrap: wrap !important;
          gap: 16px;
        }

        .pagination-info {
          font-size: 14px !important;
          color: #64748b !important;
        }

        .pagination-controls {
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
        }

        .pagination-btn {
          padding: 8px 16px !important;
          border: 1px solid #e2e8f0 !important;
          background: white !important;
          color: #475569 !important;
          border-radius: 8px !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          display: flex !important;
          align-items: center !important;
          gap: 6px !important;
        }

        .pagination-btn:disabled {
          opacity: 0.5 !important;
          cursor: not-allowed !important;
        }

        .pagination-btn:not(:disabled):hover {
          background: #f1f5f9 !important;
          border-color: #cbd5e1 !important;
        }

        .page-numbers {
          display: flex !important;
          gap: 4px !important;
        }

        .page-btn {
          min-width: 40px !important;
          height: 40px !important;
          border: 1px solid #e2e8f0 !important;
          background: white !important;
          color: #475569 !important;
          border-radius: 8px !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
        }

        .page-btn.active {
          background: #3b82f6 !important;
          border-color: #3b82f6 !important;
          color: white !important;
        }

        .page-btn:not(.active):hover {
          background: #f1f5f9 !important;
        }

        .page-dots {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          min-width: 40px !important;
          color: #94a3b8 !important;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .branch-report-container {
            padding: 16px !important;
          }

          .report-header {
            flex-direction: column !important;
            align-items: stretch !important;
          }

          .header-left {
            min-width: auto !important;
          }

          .header-actions {
            flex-direction: column !important;
            align-items: stretch !important;
          }

          .search-box {
            min-width: auto !important;
          }

          .stats-summary {
            grid-template-columns: 1fr !important;
          }

          .report-table th,
          .report-table td {
            padding: 12px 8px !important;
            font-size: 12px !important;
          }

          .pagination-container {
            flex-direction: column !important;
            align-items: stretch !important;
          }

          .pagination-controls {
            justify-content: center !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BranchReportLayer;